import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";

// Searchable single-select with a free-text "Add …" affordance. Used for
// Country and City pickers so the standard lists are selectable yet anything
// missing can still be typed in.
export function ComboBox({ value, onChange, options, placeholder, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const qq = query.trim().toLowerCase();
    const base = qq ? options.filter((o) => o.toLowerCase().includes(qq)) : options;
    return base.slice(0, 300);
  }, [options, query]);
  const exact = options.some((o) => o.toLowerCase() === query.trim().toLowerCase());
  return (
    <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled}
          className="w-full justify-between font-normal" data-testid={testid}>
          <span className="truncate">{value || <span className="text-zinc-400">{placeholder}</span>}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search…" value={query} onValueChange={setQuery} />
          <CommandList className="max-h-[min(300px,40vh)] overflow-y-auto overscroll-contain touch-pan-y">
            <CommandEmpty>Type to add a custom value</CommandEmpty>
            <CommandGroup>
              {filtered.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => { onChange(o); setOpen(false); setQuery(""); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === o ? "opacity-100" : "opacity-0"}`} />{o}
                </CommandItem>
              ))}
              {query.trim() && !exact && (
                <CommandItem value={`__add__${query}`} onSelect={() => { onChange(query.trim()); setOpen(false); setQuery(""); }} data-testid={`${testid}-add`}>
                  <Plus className="mr-2 h-4 w-4" />Add “{query.trim()}”
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ComboBox;
